import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';
import { _Function } from './functions-v1-function-functions.types';

function resolveWixFunctionsApiV1FunctionsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/functions/v1/functions',
        destPath: '/v1/functions',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/functions/v1/functions',
        destPath: '/v1/functions',
      },
      {
        srcPath: '/functions/v1/bulk/functions',
        destPath: '/v1/bulk/functions',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/functions/v1/functions',
        destPath: '/v1/functions',
      },
      {
        srcPath: '/functions/v1/bulk/functions',
        destPath: '/v1/bulk/functions',
      },
    ],
    _: [
      {
        srcPath: '/functions/v1/functions',
        destPath: '/v1/functions',
      },
      {
        srcPath: '/functions/v1/bulk/functions',
        destPath: '/v1/bulk/functions',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_functions_functions';

/**
 * Creates a function with the specified configuration. After creation, you'll need to attach automation logic using the Function Methods API and configure any required service plugin settings before the function can be activated.
 *
 * This isn't the recommended way to create a function. Learn more about [function creation](https://dev.wix.com/docs/api-reference/business-management/functions/about-function-creation-and-activation#quick-creation-and-configuration).
 */
export function createFunction(payload: object): RequestOptionsFactory<any> {
  function __createFunction({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'function.createdDate' },
          { path: 'function.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.functions.v1.function',
      method: 'POST' as any,
      methodFqn: 'wix.functions.api.v1.Functions.CreateFunction',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFunctionsApiV1FunctionsUrl({
        protoPath: '/v1/functions',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'function.createdDate' },
              { path: 'function.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createFunction;
}

/** Retrieves a function. */
export function getFunction(payload: object): RequestOptionsFactory<any> {
  function __getFunction({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.functions.v1.function',
      method: 'GET' as any,
      methodFqn: 'wix.functions.api.v1.Functions.GetFunction',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFunctionsApiV1FunctionsUrl({
        protoPath: '/v1/functions/{functionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'function.createdDate' },
              { path: 'function.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getFunction;
}

/**
 * Updates a function.
 *
 * Each time the function is updated,
 * `revision` increments by 1.
 * The existing `revision` must be passed when updating the function.
 * This ensures you're working with the latest version of the function
 * and prevents unintended overwrites.
 */
export function updateFunction(payload: object): RequestOptionsFactory<any> {
  function __updateFunction({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'function.createdDate' },
          { path: 'function.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.functions.v1.function',
      method: 'PATCH' as any,
      methodFqn: 'wix.functions.api.v1.Functions.UpdateFunction',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFunctionsApiV1FunctionsUrl({
        protoPath: '/v1/functions/{function.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'function.createdDate' },
              { path: 'function.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateFunction;
}

/**
 * Deletes a function.
 *
 * Deleting a function permanently removes it from the Function List
 * in the site's dashboard.
 */
export function deleteFunction(payload: object): RequestOptionsFactory<any> {
  function __deleteFunction({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.functions.v1.function',
      method: 'DELETE' as any,
      methodFqn: 'wix.functions.api.v1.Functions.DeleteFunction',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFunctionsApiV1FunctionsUrl({
        protoPath: '/v1/functions/{functionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteFunction;
}

/**
 * Creates a query to retrieve a list of functions.
 *
 * The Query Functions method builds a query to retrieve a list of functions and returns a `FunctionsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `FunctionsQueryBuilder` methods onto the query. `FunctionsQueryBuilder` methods enable you to filter, sort, and control the results that Query Functions returns.
 *
 * Query Functions has a default paging limit of 50, which you can override.
 *
 *  For a full description of the item object, see the object returned for the `items` property in `FunctionsQueryResult`.
 */
export function queryFunctions(payload: object): RequestOptionsFactory<any> {
  function __queryFunctions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.functions.v1.function',
      method: 'GET' as any,
      methodFqn: 'wix.functions.api.v1.Functions.QueryFunctions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFunctionsApiV1FunctionsUrl({
        protoPath: '/v1/functions/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'functions.createdDate' },
              { path: 'functions.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixFunctionsApiV1FunctionsUrl({
            protoPath: '/v1/functions/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryFunctions;
}

/**
 * Synchronously update tags on multiple functions.
 * A tag that appears both the list to assign and the list to unassign tags, will be assigned.
 */
export function bulkUpdateFunctionTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateFunctionTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.functions.v1.function',
      method: 'POST' as any,
      methodFqn: 'wix.functions.api.v1.Functions.BulkUpdateFunctionTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFunctionsApiV1FunctionsUrl({
        protoPath: '/v1/bulk/functions/bulk-update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateFunctionTags;
}

/**
 * Asynchronously update tags on multiple functions.
 * An empty filter updates all functions.
 * A tag that appears both the list to assign and the list to unassign tags, will be assigned.
 */
export function bulkUpdateFunctionTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateFunctionTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.functions.v1.function',
      method: 'POST' as any,
      methodFqn:
        'wix.functions.api.v1.Functions.BulkUpdateFunctionTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFunctionsApiV1FunctionsUrl({
        protoPath: '/v1/bulk/functions/update-tags-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateFunctionTagsByFilter;
}
